import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useApp, LANG_SHORT } from "@/context/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Languages, Moon, Sun, Building2, ArrowRight, X, UserPlus } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t, cycleLang, lang, theme, toggleTheme } = useApp();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState(localStorage.getItem("rememberedIdentifier") || "");
  const [pin, setPin] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(null);   // null | "pin" | "identifier"

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await login(identifier, pin, remember);
      toast.success(t("hello"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("login_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-dark via-brand to-brand text-white relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 zellige-overlay pointer-events-none" />
      <div className="absolute -top-24 -right-20 h-80 w-80 rounded-full bg-majorelle/30 blur-[110px] pointer-events-none" />
      <div className="absolute -bottom-28 -left-20 h-80 w-80 rounded-full bg-emerald-400/20 blur-[110px] pointer-events-none" />
      <div className="relative z-10 flex justify-end gap-2 p-4">
        <button onClick={cycleLang} data-testid="login-lang-toggle"
          className="h-10 px-3 rounded-full bg-white/15 font-semibold text-sm flex items-center gap-1.5 [&>*]:pointer-events-none">
          <Languages className="h-4 w-4" />{LANG_SHORT[lang]}
        </button>
        <button onClick={toggleTheme} data-testid="login-theme-toggle"
          className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
          {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </button>
      </div>

      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 max-w-md w-full mx-auto pb-10">
        <div className="h-16 w-16 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center mb-6 shadow-ios animate-rise">
          <Building2 className="h-9 w-9" />
        </div>
        <h1 className="text-4xl font-black tracking-tight mb-2">{t("login_title")}</h1>
        <p className="text-white/70 text-lg mb-8">{t("login_subtitle")}</p>

        <div className="rounded-3xl bg-white/10 backdrop-blur-xl border border-white/15 p-5 shadow-ios-lg">
        <form onSubmit={submit} className="space-y-3">
          <input
            type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
            placeholder={t("login_id_ph")} data-testid="login-identifier"
            autoCapitalize="none" autoCorrect="off" spellCheck="false"
            className="w-full h-14 rounded-2xl bg-white text-foreground px-5 text-base outline-none focus:ring-2 focus:ring-white/60 transition-shadow"
            required
          />
          <input
            type="password" inputMode="numeric" maxLength={6} value={pin}
            onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder={t("login_pin_ph")} data-testid="login-pin"
            className="w-full h-14 rounded-2xl bg-white text-foreground px-5 text-base outline-none tracking-[0.4em] focus:ring-2 focus:ring-white/60 transition-shadow"
            required
          />
          <label className="flex items-center gap-2 text-sm text-white/80 px-1 cursor-pointer">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
              data-testid="login-remember" className="h-4 w-4 rounded accent-majorelle" />
            {t("login_remember")}
          </label>
          <button type="submit" disabled={busy} data-testid="login-submit"
            className="w-full h-14 rounded-full bg-gradient-to-br from-majorelle to-[#243b8f] text-white font-bold text-lg flex items-center justify-center gap-2 shadow-[0_12px_30px_-8px_rgba(50,84,198,0.7)] active:scale-[0.98] hover:brightness-110 transition-all disabled:opacity-60">
            {busy ? t("loading") : t("login")}
            {!busy && <ArrowRight className="h-5 w-5 rtl:rotate-180" />}
          </button>
        </form>

        <div className="flex items-center justify-between mt-3 text-sm">
          <button onClick={() => setForgot("identifier")} data-testid="forgot-identifier-link" className="text-white/70 underline">{t("forgot_identifier")}</button>
          <button onClick={() => setForgot("pin")} data-testid="forgot-pin-link" className="text-white/70 underline">{t("forgot_pin")}</button>
        </div>

        <button onClick={() => navigate("/register")} data-testid="register-link"
          className="w-full h-12 rounded-full bg-white/10 hover:bg-white/20 transition-colors mt-6 font-semibold flex items-center justify-center gap-2 active:scale-[0.98]">
          <UserPlus className="h-4 w-4" /> {t("create_account")}
        </button>
        </div>
      </div>

      {forgot && <ForgotDialog kind={forgot} onClose={() => setForgot(null)} t={t} />}
    </div>
  );
}

// Boîte de dialogue « identifiant / PIN oublié » (prévient le président).
function ForgotDialog({ kind, onClose, t }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const isPin = kind === "pin";

  const submit = async () => {
    setBusy(true);
    try {
      if (isPin) await api.post("/auth/forgot-pin", { identifier: value.toLowerCase().trim() });
      else await api.post("/auth/forgot-identifier", { phone: value.trim() });
      toast.success(t("forgot_sent"));
      onClose();
    } catch { toast.error(t("login_error")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="forgot-dialog">
      <div className="bg-card text-foreground rounded-3xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">{isPin ? t("forgot_pin") : t("forgot_identifier")}</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-sm text-muted-foreground mb-3">{isPin ? t("forgot_pin_help") : t("forgot_identifier_help")}</p>
        <input value={value} onChange={(e) => setValue(e.target.value)}
          placeholder={isPin ? t("login_id_ph") : t("phone_ph")} data-testid="forgot-input"
          className="w-full h-12 rounded-xl border border-input bg-background px-4 outline-none" />
        <button onClick={submit} disabled={busy || !value.trim()} data-testid="forgot-submit"
          className="w-full h-12 rounded-xl bg-brand text-white font-bold mt-4 disabled:opacity-60">
          {busy ? t("loading") : t("notify_president")}
        </button>
      </div>
    </div>
  );
}
